"use client"

import { useMemo } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type { CalendarEvent } from "@/components/calendar/calendar-types"

interface WeekOverviewStripProps {
  /** Monday 00:00 of the week being shown (local time). */
  weekStart: Date
  /** The week's appointments (already cancellation-filtered by the parent). */
  events: CalendarEvent[]
  isLoading: boolean
  className?: string
}

/** Local `YYYY-MM-DD` bucket key — never `toISOString()` (that shifts to UTC). */
function dayKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

/**
 * Slim Mon→Sun band of per-day appointment counts for the doctor's own week.
 *
 * Presentational: the parent owns the week-window calendar fetch and the
 * Monday anchor, so the strip only buckets and renders. Every cell deep-links
 * to /calendar?date=yyyy-mm-dd — the calendar page opens on that day.
 */
export function WeekOverviewStrip({
  weekStart,
  events,
  isLoading,
  className,
}: WeekOverviewStripProps) {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()

  const days = useMemo(() => {
    const counts = new Map<string, number>()
    for (const event of events) {
      const start = new Date(event.start)
      if (Number.isNaN(start.getTime())) continue
      const key = dayKey(start)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }

    const todayKey = dayKey(new Date())

    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart)
      date.setDate(weekStart.getDate() + i)
      const key = dayKey(date)
      return {
        key,
        date,
        count: counts.get(key) ?? 0,
        isToday: key === todayKey,
      }
    })
  }, [events, weekStart])

  const rangeLabel = useMemo(() => {
    const first = days[0]?.date
    const last = days[days.length - 1]?.date
    if (!first || !last) return null
    const opts = { day: "numeric", month: "short" } as const
    return `${formatter.dateTime(first, opts)} – ${formatter.dateTime(last, opts)}`
  }, [days, formatter])

  return (
    <ApolloPanel
      eyebrow={t("weekOverview.title")}
      actions={
        rangeLabel ? (
          <Badge variant="outline" className="text-[10px]">
            {rangeLabel}
          </Badge>
        ) : undefined
      }
      className={className}
    >
      <div className="grid grid-cols-7 gap-1.5">
        {isLoading
          ? days.map((day) => (
              <Skeleton key={day.key} className="h-[68px] w-full rounded-md" />
            ))
          : days.map((day) => (
              <Link
                key={day.key}
                href={`/calendar?date=${day.key}`}
                aria-label={`${formatter.dateTime(day.date, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })} — ${t("weekOverview.appointments", { count: day.count })}`}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 rounded-md border px-1 py-2.5 transition-colors",
                  day.isToday
                    ? "border-primary/40 bg-primary/5"
                    : "border-border/60 hover:bg-muted/60",
                )}
              >
                <span
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-[0.12em]",
                    day.isToday ? "text-primary" : "text-text-dim",
                  )}
                >
                  {formatter.dateTime(day.date, { weekday: "short" })}
                </span>
                <span
                  className={cn(
                    "font-serif text-[22px] leading-none tabular-nums",
                    day.count === 0 && "text-text-dim",
                  )}
                >
                  {day.count}
                </span>
                <span className="text-[10.5px] tabular-nums text-text-dim">
                  {formatter.dateTime(day.date, { day: "numeric" })}
                </span>
              </Link>
            ))}
      </div>
    </ApolloPanel>
  )
}
